import React, { useState, useMemo, useEffect } from 'react';
import {
  UserPlus,
  Users,
  UserCheck,
  User,
  Building2,
  Star,
  Briefcase,
  Tag,
  MapPin,
  Filter,
  Grid3x3,
  List,
  Phone,
  Mail,
  Edit,
  Trash2,
  ChevronLeft,
  ChevronRight,
  X,
  Save,
  AlertTriangle,
  type LucideIcon,
} from 'lucide-react';
import styles from './RubricaPage.module.css';
import rubricaService, { ContattoRequest } from '../../services/rubricaService';
import { ContattoRubrica } from '../../types';
import { ExportDropdown, ExportFormat } from '@/components/common/ExportDropdown';
import { exportToExcel, exportToPdf, ExportColumn } from '@/utils/exports';
import { coloreTipoContatto } from '../../constants/tipiContatto';
import { useTipiContatto } from '@/hooks/useTipiContatto';

// Badge (icona + stile) per il classificatore del contatto.
// I tipi storici hanno uno stile dedicato; quelli creati dall'utente usano il colore configurato.
const getTipoBadgeMeta = (
  tipo?: string,
  colore?: string
): { className: string; Icon: LucideIcon; color: string; custom: boolean } => {
  switch (tipo) {
    case 'AZIENDA':
      return { className: styles.badgeInfo, Icon: Building2, color: '#1565C0', custom: false };
    case 'CLIENTE':
      return { className: styles.badgeCliente, Icon: User, color: '#3949AB', custom: false };
    case 'CLIENTE_TOP':
      return { className: styles.badgeClienteTop, Icon: Star, color: '#F57F17', custom: false };
    case 'COLLABORATORE':
      return { className: styles.badgeCollaboratore, Icon: Users, color: '#00695C', custom: false };
    case 'AGENTE':
      return { className: styles.badgeAgente, Icon: Briefcase, color: '#6A1B9A', custom: false };
    case 'PRIVATO':
      return { className: styles.badgeSuccess, Icon: UserCheck, color: '#2E7D32', custom: false };
    default:
      return { className: '', Icon: Tag, color: coloreTipoContatto(tipo, colore), custom: true };
  }
};

const TipoBadge: React.FC<{ tipo?: string; label: string; colore?: string }> = ({ tipo, label, colore }) => {
  const { className, Icon, color, custom } = getTipoBadgeMeta(tipo, colore);
  return (
    <span
      className={`${styles.badge} ${className}`}
      style={custom ? { background: `${color}1A`, color } : undefined}
    >
      <Icon size={12} />
      {label}
    </span>
  );
};

// Color gradients for avatars
const gradients = [
  'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
  'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
  'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
  'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
  'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
];

// Extended type with UI-specific fields
type ContattoExtended = ContattoRubrica & {
  avatar: string;
  gradient: string;
  tipo: string;
  movimenti: number;
};

export const RubricaPage: React.FC = () => {
  // Tipi contatto configurabili dall'utente (pagina "Tipi Contatto")
  const { tipi: tipiContatto, label: tipoContattoLabel, colore: tipoContattoColore } = useTipiContatto();
  const tipoDefault = tipiContatto[0]?.value || 'PRIVATO';

  // Data states
  const [contatti, setContatti] = useState<ContattoExtended[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // UI states
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ContattoExtended | null>(null);
  const [viewMode, setViewMode] = useState<'card' | 'list'>('list');

  // Form states
  const [editingContatto, setEditingContatto] = useState<ContattoExtended | null>(null);
  const [formData, setFormData] = useState<ContattoRequest>({
    nome: '',
    email: '',
    telefono: '',
    citta: '',
    provincia: '',
    tipoContatto: 'PRIVATO',
    codiceProgressivo: '',
    generaCodiceAutomatico: true,
    note: '',
  });
  const [formErrors, setFormErrors] = useState<{nome?: boolean; codiceProgressivo?: string}>({});
  const [exportLoading, setExportLoading] = useState(false);
  const [nextCodice, setNextCodice] = useState<string | null>(null);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [searchCodice, setSearchCodice] = useState('');
  const [tipoFilter, setTipoFilter] = useState('tutti');
  const [provinciaFilter, setProvinciaFilter] = useState('tutte');
  const [cittaFilter, setCittaFilter] = useState('tutte');
  const [sortBy, setSortBy] = useState('nome-asc');

  // Helper function to generate avatar from name
  const getAvatar = (nome: string, azienda?: string): string => {
    const displayName = azienda || nome;
    const parts = displayName.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return displayName.substring(0, 2).toUpperCase();
  };

  // Helper function to get gradient for contact
  const getGradient = (id: number): string => {
    return gradients[id % gradients.length];
  };

  // Helper function to determine contact type from the tipoContatto classifier
  const getContactType = (contatto: ContattoRubrica): string => {
    return contatto.tipoContatto || 'PRIVATO';
  };

  // Transform API data to extended format with UI fields
  const transformContatto = (contatto: ContattoRubrica): ContattoExtended => {
    return {
      ...contatto,
      avatar: getAvatar(contatto.nome, contatto.azienda),
      gradient: getGradient(contatto.id),
      tipo: getContactType(contatto),
      movimenti: contatto.numeroMovimenti || 0,
    };
  };

  // Load contatti from API
  const loadContatti = async (searchName?: string, searchCode?: string) => {
    try {
      setLoading(true);
      setError(null);
      const filters: { nome?: string; codice?: string } = {};
      if (searchName) filters.nome = searchName;
      if (searchCode) filters.codice = searchCode;
      const data = await rubricaService.getContatti(Object.keys(filters).length > 0 ? filters : undefined);
      const transformedData = data.map(transformContatto);
      setContatti(transformedData);
    } catch (err) {
      console.error('Error loading contatti:', err);
      setError('Errore nel caricamento dei contatti');
    } finally {
      setLoading(false);
    }
  };

  // Load data on mount
  useEffect(() => {
    loadContatti();
  }, []);

  // Reload when search term changes (with debounce would be better)
  useEffect(() => {
    const timer = setTimeout(() => {
      loadContatti(searchTerm || undefined, searchCodice || undefined);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, searchCodice]);

  // Get unique provinces and cities for filter options
  const province = useMemo(() => {
    const uniqueProvinces = [...new Set(contatti.map(c => c.provincia).filter(Boolean))].sort();
    return uniqueProvinces as string[];
  }, [contatti]);

  const citta = useMemo(() => {
    const uniqueCitta = [...new Set(contatti.map(c => c.citta).filter(Boolean))].sort();
    return uniqueCitta as string[];
  }, [contatti]);

  // Filter and sort contacts (client-side filtering for tipo, provincia, citta)
  const filteredContatti = useMemo(() => {
    let result = contatti.filter(c => {
      // Type filter
      if (tipoFilter !== 'tutti' && c.tipo !== tipoFilter) {
        return false;
      }
      // Province filter
      if (provinciaFilter !== 'tutte' && c.provincia !== provinciaFilter) {
        return false;
      }
      // City filter
      if (cittaFilter !== 'tutte' && c.citta !== cittaFilter) {
        return false;
      }
      return true;
    });

    // Sort
    result.sort((a, b) => {
      switch (sortBy) {
        case 'nome-asc':
          return a.nome.localeCompare(b.nome);
        case 'nome-desc':
          return b.nome.localeCompare(a.nome);
        case 'movimenti':
          return b.movimenti - a.movimenti;
        default:
          return 0;
      }
    });

    return result;
  }, [contatti, tipoFilter, provinciaFilter, cittaFilter, sortBy]);

  // Stats based on all contacts: un contatore per ogni tipo del classificatore
  const stats = useMemo(() => ({
    totale: contatti.length,
    perTipo: tipiContatto
      .map(t => ({
        value: t.value,
        label: t.label,
        colore: t.colore,
        count: contatti.filter(c => c.tipo === t.value).length,
      }))
      .filter(t => t.count > 0),
    province: new Set(contatti.map(c => c.provincia).filter(Boolean)).size,
  }), [contatti, tipiContatto]);

  const handleDeleteClick = (contatto: ContattoExtended) => {
    setDeleteTarget(contatto);
    setShowDeleteModal(true);
  };

  const handleEditClick = (contatto: ContattoExtended) => {
    setEditingContatto(contatto);
    setFormData({
      nome: contatto.nome,
      email: contatto.email || '',
      telefono: contatto.telefono || '',
      citta: contatto.citta || '',
      provincia: contatto.provincia || '',
      partitaIva: contatto.partitaIva || '',
      codiceFiscale: contatto.codiceFiscale || '',
      tipoContatto: contatto.tipoContatto || tipoDefault,
      codiceProgressivo: contatto.codiceProgressivo || '',
      generaCodiceAutomatico: false, // In modifica non genera automaticamente
      note: contatto.note || '',
    });
    setShowModal(true);
  };

  const handleConfirmDelete = async () => {
    if (deleteTarget) {
      try {
        await rubricaService.deleteContatto(deleteTarget.id);
        // Reload the list after deletion
        await loadContatti(searchTerm || undefined, searchCodice || undefined);
      } catch (err) {
        console.error('Error deleting contact:', err);
        setError('Errore durante l\'eliminazione del contatto');
      }
    }
    setShowDeleteModal(false);
    setDeleteTarget(null);
  };

  const handleExport = async (format: ExportFormat) => {
    if (filteredContatti.length === 0) {
      alert('Nessun dato da esportare');
      return;
    }

    setExportLoading(true);
    try {
      const columns: ExportColumn[] = [
        { field: 'codiceProgressivo', header: 'Codice', type: 'text', width: 10 },
        { field: 'nome', header: 'Nome/Azienda', type: 'text', width: 25 },
        { field: 'tipoDisplay', header: 'Tipo', type: 'text', width: 10 },
        { field: 'email', header: 'Email', type: 'text', width: 20 },
        { field: 'telefono', header: 'Telefono', type: 'text', width: 15 },
        { field: 'partitaIva', header: 'P.IVA', type: 'text', width: 15 },
        { field: 'codiceFiscale', header: 'Codice Fiscale', type: 'text', width: 18 },
        { field: 'citta', header: 'Città', type: 'text', width: 15 },
        { field: 'provincia', header: 'Prov.', type: 'text', width: 6 },
      ];

      const exportData = filteredContatti.map(c => ({
        codiceProgressivo: c.codiceProgressivo || '',
        nome: c.nome,
        tipoDisplay: tipoContattoLabel(c.tipoContatto),
        email: c.email || '',
        telefono: c.telefono || '',
        partitaIva: c.partitaIva || '',
        codiceFiscale: c.codiceFiscale || '',
        citta: c.citta || '',
        provincia: c.provincia || '',
      }));

      const config = {
        title: 'Rubrica',
        filename: 'rubrica',
        columns,
        orientation: 'landscape' as const,
      };

      if (format === 'excel') {
        await exportToExcel(config, exportData);
      } else {
        await exportToPdf(config, exportData);
      }
    } catch (err) {
      console.error('Error exporting rubrica:', err);
      setError('Errore durante l\'esportazione');
    } finally {
      setExportLoading(false);
    }
  };

  const handleFormChange = (field: keyof ContattoRequest, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleOpenNewModal = async () => {
    setEditingContatto(null);
    setFormData({ nome: '', email: '', telefono: '', citta: '', provincia: '', tipoContatto: tipoDefault, codiceProgressivo: '', generaCodiceAutomatico: true, note: '' });
    setNextCodice(null);
    setShowModal(true);
    try {
      const codice = await rubricaService.getNextCodice();
      setNextCodice(codice);
    } catch (err) {
      console.error('Error loading next codice:', err);
    }
  };

  const handleFormSubmit = async () => {
    // Validate required fields
    const errors: {nome?: boolean; codiceProgressivo?: string} = {};
    if (!formData.nome.trim()) errors.nome = true;

    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    try {
      if (editingContatto) {
        await rubricaService.updateContatto(editingContatto.id, formData);
      } else {
        await rubricaService.createContatto(formData);
      }

      // Reset form and close modal
      setFormData({
        nome: '',
        email: '',
        telefono: '',
        citta: '',
        provincia: '',
        tipoContatto: tipoDefault,
        codiceProgressivo: '',
        generaCodiceAutomatico: true,
        note: '',
      });
      setEditingContatto(null);
      setFormErrors({});
      setShowModal(false);

      // Reload the list
      await loadContatti(searchTerm || undefined, searchCodice || undefined);
    } catch (err: unknown) {
      console.error('Error saving contact:', err);
      // Check if it's a duplicate code error
      const axiosError = err as { response?: { data?: { details?: { fieldName?: string }; message?: string } } };
      if (axiosError.response?.data?.details?.fieldName === 'codiceProgressivo') {
        setFormErrors(prev => ({ ...prev, codiceProgressivo: 'Codice cliente già esistente' }));
      } else {
        setError('Errore durante il salvataggio del contatto');
      }
    }
  };

  return (
    <div className={styles.container}>
      {/* Error Display */}
      {error && (
        <div style={{
          padding: '12px',
          marginBottom: '16px',
          backgroundColor: 'var(--error-light)',
          color: 'var(--error-dark)',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <AlertTriangle size={20} />
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            <X size={18} />
          </button>
        </div>
      )}

      {/* Header */}
      <div className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Rubrica</h1>
        <div className={styles.pageActions}>
          <ExportDropdown
            onExport={handleExport}
            disabled={filteredContatti.length === 0}
            loading={exportLoading}
          />
          <button className={styles.btnPrimary} onClick={handleOpenNewModal}>
            <UserPlus size={18} />
            <span className={styles.btnLabel}>Nuovo</span>
          </button>
        </div>
      </div>

      {/* Stats Bar */}
      <div className={styles.statsBar}>
        <div className={styles.statItem}>
          <Users size={20} style={{ color: 'var(--primary-main)' }} />
          <span className={styles.statNumber}>{stats.totale}</span>
          <span className={styles.statText}>Contatti</span>
        </div>
        {stats.perTipo.map((t) => {
          const { Icon, color } = getTipoBadgeMeta(t.value, t.colore);
          const attivo = tipoFilter === t.value;
          return (
            <React.Fragment key={t.value}>
              <div className={styles.statDivider} />
              <button
                type="button"
                className={`${styles.statItem} ${styles.statButton} ${attivo ? styles.statButtonActive : ''}`}
                onClick={() => setTipoFilter(attivo ? 'tutti' : t.value)}
                title={attivo ? 'Rimuovi il filtro' : `Filtra per ${t.label}`}
              >
                <Icon size={20} style={{ color }} />
                <span className={styles.statNumber}>{t.count}</span>
                <span className={styles.statText}>{t.label}</span>
              </button>
            </React.Fragment>
          );
        })}
        <div className={styles.statDivider} />
        <div className={styles.statItem}>
          <MapPin size={20} style={{ color: 'var(--warning-main)' }} />
          <span className={styles.statNumber}>{stats.province}</span>
          <span className={styles.statText}>Province</span>
        </div>
      </div>

      {/* Filters Card */}
      <div className={styles.filtersCard}>
        <div className={styles.filtersHeader}>
          <Filter size={16} />
          <span>Filtri e Ricerca</span>
        </div>
        <div className={styles.filtersGrid}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Cerca Nome</label>
            <input
              type="text"
              placeholder="Nome contatto..."
              className={styles.formControl}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Codice Cliente</label>
            <input
              type="text"
              placeholder="Es. 26/0001"
              className={styles.formControl}
              value={searchCodice}
              onChange={(e) => setSearchCodice(e.target.value)}
            />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Tipo</label>
            <select
              className={styles.formControl}
              value={tipoFilter}
              onChange={(e) => setTipoFilter(e.target.value)}
            >
              <option value="tutti">Tutti</option>
              {tipiContatto.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Provincia</label>
            <select
              className={styles.formControl}
              value={provinciaFilter}
              onChange={(e) => setProvinciaFilter(e.target.value)}
            >
              <option value="tutte">Tutte</option>
              {province.map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Città</label>
            <select
              className={styles.formControl}
              value={cittaFilter}
              onChange={(e) => setCittaFilter(e.target.value)}
            >
              <option value="tutte">Tutte</option>
              {citta.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Ordina per</label>
            <select
              className={styles.formControl}
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="nome-asc">Nome (A-Z)</option>
              <option value="nome-desc">Nome (Z-A)</option>
              <option value="movimenti">Movimenti</option>
            </select>
          </div>
        </div>
      </div>

      {/* Results Bar with View Toggle */}
      <div className={styles.resultsBar}>
        <span className={styles.resultsInfo}>
          {loading ? 'Caricamento...' : `${filteredContatti.length} contatti`}
        </span>
        <div className={styles.viewToggle}>
          <button
            className={`${styles.btnIcon} ${viewMode === 'list' ? styles.active : ''}`}
            onClick={() => setViewMode('list')}
            title="Vista lista"
          >
            <List size={18} />
          </button>
          <button
            className={`${styles.btnIcon} ${viewMode === 'card' ? styles.active : ''}`}
            onClick={() => setViewMode('card')}
            title="Vista griglia"
          >
            <Grid3x3 size={18} />
          </button>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          color: 'var(--text-secondary)'
        }}>
          Caricamento contatti...
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredContatti.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          color: 'var(--text-secondary)'
        }}>
          Nessun contatto trovato
        </div>
      )}

      {/* LIST VIEW */}
      {!loading && viewMode === 'list' && filteredContatti.length > 0 && (
        <div className={styles.tableCard}>
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Codice</th>
                  <th>Contatto</th>
                  <th className={styles.hideOnMobile}>Tipo</th>
                  <th className={styles.hideOnMobile}>Città</th>
                  <th className={styles.hideOnTablet}>Telefono</th>
                  <th className={styles.hideOnTablet}>Email</th>
                  <th className={styles.textRight}>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {filteredContatti.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <span style={{ fontFamily: 'monospace', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                        {c.codiceProgressivo || '--'}
                      </span>
                    </td>
                    <td>
                      <div className={styles.contactCell}>
                        <div className={styles.avatar} style={{ background: c.gradient }}>{c.avatar}</div>
                        <div>
                          <div className={styles.contactName}>{c.nome}</div>
                          <div className={styles.contactPiva}>{c.partitaIva || c.codiceFiscale || '--'}</div>
                        </div>
                      </div>
                    </td>
                    <td className={styles.hideOnMobile}>
                      <TipoBadge tipo={c.tipo} label={tipoContattoLabel(c.tipo)} colore={tipoContattoColore(c.tipo)} />
                    </td>
                    <td className={styles.hideOnMobile}>{c.citta}</td>
                    <td className={styles.hideOnTablet}>
                      {c.telefono !== '--' ? (
                        <a href={`tel:${c.telefono}`} className={styles.link}><Phone size={14} /> {c.telefono}</a>
                      ) : '--'}
                    </td>
                    <td className={styles.hideOnTablet}>
                      <a href={`mailto:${c.email}`} className={styles.link}><Mail size={14} /> {c.email}</a>
                    </td>
                    <td className={styles.textRight}>
                      <div className={styles.rowActions}>
                        <button className={styles.btnIconSmall} onClick={() => handleEditClick(c)}><Edit size={16} /></button>
                        <button className={styles.btnIconSmall} onClick={() => handleDeleteClick(c)}><Trash2 size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* CARD VIEW - Fluid Grid */}
      {!loading && viewMode === 'card' && filteredContatti.length > 0 && (
        <div className={styles.contactsGrid}>
          {filteredContatti.map((c) => (
            <div key={c.id} className={styles.contactCard}>
              <div className={styles.cardTop}>
                <div className={styles.avatar} style={{ background: c.gradient }}>{c.avatar}</div>
                <TipoBadge tipo={c.tipo} label={tipoContattoLabel(c.tipo)} colore={tipoContattoColore(c.tipo)} />
              </div>
              <div style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                {c.codiceProgressivo || '--'}
              </div>
              <h4 className={styles.cardName}>{c.nome}</h4>
              <div className={styles.cardLocation}>
                <MapPin size={12} /> {c.citta || '--'}{c.provincia ? `, ${c.provincia}` : ''}
              </div>
              <div className={styles.cardContacts}>
                {c.telefono && <a href={`tel:${c.telefono}`} className={styles.cardLink}><Phone size={14} /></a>}
                {c.email && <a href={`mailto:${c.email}`} className={styles.cardLink}><Mail size={14} /></a>}
              </div>
              <div className={styles.cardFooter}>
                <span className={styles.cardMovimenti}>{c.movimenti} movimenti</span>
                <div className={styles.cardActions}>
                  <button className={styles.btnIconSmall} onClick={() => handleEditClick(c)}><Edit size={14} /></button>
                  <button className={styles.btnIconSmall} onClick={() => handleDeleteClick(c)}><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      <div className={styles.pagination}>
        <span className={styles.paginationInfo}>1-{filteredContatti.length} di {filteredContatti.length}</span>
        <div className={styles.paginationButtons}>
          <button className={styles.btnIcon} disabled><ChevronLeft size={18} /></button>
          <button className={styles.btnIcon}><ChevronRight size={18} /></button>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className={styles.modalOverlay} onMouseDown={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>{editingContatto ? 'Modifica Contatto' : 'Nuovo Contatto'}</h3>
              <button className={styles.modalClose} onClick={() => { setShowModal(false); setEditingContatto(null); }}><X size={20} /></button>
            </div>
            <div className={styles.modalBody}>
              {/* Codice Cliente con toggle automatico/manuale */}
              {!editingContatto && (
                <div className={styles.formRow}>
                  <div className={styles.formGroup} style={{ flex: 1 }}>
                    <label className={styles.formLabel}>Codice Cliente</label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <input
                        type="text"
                        className={`${styles.formControl} ${formErrors.codiceProgressivo ? styles.error : ''}`}
                        placeholder={formData.generaCodiceAutomatico ? (nextCodice || "Caricamento...") : "Es. 26/1700"}
                        value={formData.generaCodiceAutomatico ? (nextCodice || '') : (formData.codiceProgressivo || '')}
                        disabled={formData.generaCodiceAutomatico}
                        onChange={(e) => { setFormData(prev => ({ ...prev, codiceProgressivo: e.target.value })); setFormErrors(prev => ({...prev, codiceProgressivo: undefined})); }}
                        style={{
                          fontFamily: 'monospace',
                          backgroundColor: formData.generaCodiceAutomatico ? 'var(--background-alt)' : 'var(--background-paper)',
                          flex: 1
                        }}
                      />
                      <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                        <input
                          type="checkbox"
                          checked={formData.generaCodiceAutomatico}
                          onChange={(e) => { setFormData(prev => ({ ...prev, generaCodiceAutomatico: e.target.checked, codiceProgressivo: '' })); setFormErrors(prev => ({...prev, codiceProgressivo: undefined})); }}
                        />
                        Auto
                      </label>
                    </div>
                    {formErrors.codiceProgressivo && <span className={styles.errorText}>{formErrors.codiceProgressivo}</span>}
                  </div>
                </div>
              )}
              {/* Codice Progressivo (readonly, solo in modifica) */}
              {editingContatto && editingContatto.codiceProgressivo && (
                <div className={styles.formRow}>
                  <div className={styles.formGroup} style={{ flex: 1 }}>
                    <label className={styles.formLabel}>Codice Cliente</label>
                    <input
                      type="text"
                      className={styles.formControl}
                      value={editingContatto.codiceProgressivo}
                      disabled
                      style={{ fontFamily: 'monospace', backgroundColor: 'var(--background-alt)', cursor: 'not-allowed' }}
                    />
                  </div>
                </div>
              )}
              <div className={styles.formRow}>
                <div className={styles.formGroup} style={{ flex: 2 }}>
                  <label className={styles.formLabel}>Nome o Azienda *</label>
                  <input
                    type="text"
                    className={`${styles.formControl} ${formErrors.nome ? styles.error : ''}`}
                    placeholder={formData.tipoContatto === 'AZIENDA' ? "Ragione sociale" : "Nome e cognome"}
                    value={formData.nome}
                    onChange={(e) => { handleFormChange('nome', e.target.value); setFormErrors(prev => ({...prev, nome: false})); }}
                  />
                  {formErrors.nome && <span className={styles.errorText}>Campo obbligatorio</span>}
                </div>
                <div className={styles.formGroup} style={{ flex: 1 }}>
                  <label className={styles.formLabel}>Tipo</label>
                  <select
                    className={styles.formControl}
                    value={formData.tipoContatto || tipoDefault}
                    onChange={(e) => setFormData(prev => ({ ...prev, tipoContatto: e.target.value }))}
                  >
                    {tipiContatto.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                    {formData.tipoContatto && !tipiContatto.some(t => t.value === formData.tipoContatto) && (
                      <option value={formData.tipoContatto}>{tipoContattoLabel(formData.tipoContatto)}</option>
                    )}
                  </select>
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Email</label>
                  <input
                    type="email"
                    className={styles.formControl}
                    placeholder="email@esempio.com"
                    value={formData.email || ''}
                    onChange={(e) => handleFormChange('email', e.target.value)}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Telefono</label>
                  <input
                    type="tel"
                    className={styles.formControl}
                    placeholder="+39 ..."
                    value={formData.telefono || ''}
                    onChange={(e) => handleFormChange('telefono', e.target.value)}
                  />
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Partita IVA</label>
                  <input
                    type="text"
                    className={styles.formControl}
                    placeholder="Partita IVA"
                    value={formData.partitaIva || ''}
                    onChange={(e) => handleFormChange('partitaIva', e.target.value)}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Codice Fiscale</label>
                  <input
                    type="text"
                    className={styles.formControl}
                    placeholder="Codice Fiscale"
                    value={formData.codiceFiscale || ''}
                    onChange={(e) => handleFormChange('codiceFiscale', e.target.value)}
                  />
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Città</label>
                  <input
                    type="text"
                    className={styles.formControl}
                    placeholder="Città"
                    value={formData.citta || ''}
                    onChange={(e) => handleFormChange('citta', e.target.value)}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Provincia</label>
                  <input
                    type="text"
                    className={styles.formControl}
                    placeholder="XX"
                    maxLength={2}
                    value={formData.provincia || ''}
                    onChange={(e) => handleFormChange('provincia', e.target.value)}
                  />
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={styles.formGroup} style={{ flex: 1 }}>
                  <label className={styles.formLabel}>Note</label>
                  <textarea
                    className={styles.formControl}
                    placeholder="Note aggiuntive..."
                    value={formData.note || ''}
                    onChange={(e) => handleFormChange('note', e.target.value)}
                    rows={3}
                    style={{ resize: 'vertical' }}
                  />
                </div>
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.btnSecondary} onClick={() => setShowModal(false)}>Annulla</button>
              <button className={styles.btnPrimary} onClick={handleFormSubmit}>
                <Save size={16} /> Salva
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deleteTarget && (
        <div className={styles.modalOverlay} onMouseDown={(e) => { if (e.target === e.currentTarget) setShowDeleteModal(false); }}>
          <div className={styles.modalConfirm} onClick={(e) => e.stopPropagation()}>
            <div className={styles.confirmIcon}>
              <AlertTriangle size={40} />
            </div>
            <h3 className={styles.confirmTitle}>Eliminare questo contatto?</h3>
            <p className={styles.confirmText}>
              Stai per eliminare <strong>{deleteTarget.azienda || deleteTarget.nome}</strong>.
              Questa azione non può essere annullata.
            </p>
            <div className={styles.confirmActions}>
              <button className={styles.btnSecondary} onClick={() => setShowDeleteModal(false)}>
                Annulla
              </button>
              <button className={styles.btnDanger} onClick={handleConfirmDelete}>
                <Trash2 size={16} />
                Elimina
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
